/**
 * Loading, empty and error states.
 *
 * Every list and detail screen renders one of these rather than leaving a
 * blank region, so a user is never left wondering whether the app is broken
 * or the data simply is not there.
 */
import { Button } from './Button.jsx';
import { Icon, hasIcon } from './Icon.jsx';

import { useTranslation } from 'react-i18next';
export function Spinner({ large, size, label }) {
  const { t } = useTranslation();
  const big = large || size === 'lg' || size === 'large' || (typeof size === 'number' && size > 20);
  return <span className={`spinner ${big ? 'spinner-lg' : ''}`} role="status" aria-label={label || t('states.loadingShort')} />;
}

/**
 * Thin bar at the top of the viewport during a background refetch.
 * (`isFetching` is another name some screens use for `active`.)
 */
export function RouteProgress({ active, isFetching }) {
  const { t } = useTranslation();
  if (!(active ?? isFetching)) return null;
  return <div className="route-progress" role="status" aria-label={t('states.loadingShort')} />;
}

export function LoadingState({ label, message }) {
  const { t } = useTranslation();
  return (
    <div className="state">
      <Spinner large label={label || message || t('states.loadingShort')} />
      <p className="state-text">{label || message || t('states.loading')}</p>
    </div>
  );
}

/**
 * Skeleton table rows.
 *
 * Preferred over a spinner for tables: it keeps the page height stable, so the
 * layout does not jump when the data lands.
 */
export function TableSkeleton({ rows = 6, columns = 5 }) {
  return (
    <tbody aria-hidden="true">
      {Array.from({ length: rows }).map((_row, rowIndex) => (
        <tr key={rowIndex}>
          {Array.from({ length: columns }).map((_col, colIndex) => (
            <td key={colIndex}>
              <div className="skeleton" style={{ width: `${55 + ((rowIndex * 7 + colIndex * 13) % 40)}%` }} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

/** The characters screens pass as an empty state's icon, and the icon each means. */
const GLYPHS = {
  '✓': 'success', // ✓
  '✔': 'success',
  '?': 'compass',
  '✖': 'circle-x', // ✖
  '✕': 'circle-x',
  '!': 'warning',
  '⚠': 'warning', // ⚠
  '⚑': 'building', // ⚑
  '✉': 'inbox', // ✉
};

/**
 * The glyph in an empty or error state: an icon from the set when `icon`
 * names one, stands for one, or is not given; otherwise the character passed.
 */
function StateIcon({ icon: rawIcon, fallback, variant }) {
  const icon = typeof rawIcon === 'string' && GLYPHS[rawIcon.trim()] ? GLYPHS[rawIcon.trim()] : rawIcon;
  const named = typeof icon === 'string' && hasIcon(icon) ? icon : null;
  const useDefault = icon === undefined || icon === null || icon === '—';
  return (
    <div className={`state-icon ${variant ? `state-icon-${variant}` : ''}`} aria-hidden="true">
      {named || useDefault ? <Icon name={named ?? fallback} size={22} strokeWidth={1.6} /> : icon}
    </div>
  );
}

export function EmptyState({ icon, title, message, description, action }) {
  const text = message ?? description;
  return (
    <div className="state">
      <StateIcon icon={icon} fallback="inbox" />
      {title && <p className="state-title">{title}</p>}
      {text && <p className="state-text">{text}</p>}
      {action}
    </div>
  );
}

/**
 * Error state.
 *
 * The message comes from the API's own error envelope, so a user sees "Only an
 * approved request can become an order" rather than "Request failed". The
 * request id is shown for a 500, because that is the only case where a user
 * reporting it actually helps someone find the cause.
 */
export function ErrorState({ error, message, onRetry, title }) {
  const { t } = useTranslation();
  const isServerError = error?.status >= 500 || error?.status === 0;
  const displayMessage =
    error?.message ??
    (typeof message === 'string' && message.trim() ? message : null) ??
    t('errors.unexpected');

  return (
    <div className="state" role="alert">
      <StateIcon icon={error?.status === 0 ? 'refresh' : 'error'} fallback="error" variant="danger" />
      <p className="state-title">{title ?? (isServerError ? t('errors.somethingWentWrong') : t('errors.couldNotLoad'))}</p>
      <p className="state-text">{displayMessage}</p>

      {isServerError && error?.requestId && <p className="text-xs subtle mono state-ref">{t('errors.reference', { id: error.requestId, bilingual: false })}</p>}

      {onRetry && (
        <Button variant="default" size="sm" onClick={onRetry} icon={<Icon name="refresh" size={14} />}>
          {t('actions.retry')}
        </Button>
      )}
    </div>
  );
}

const ALERT_ICONS = {
  danger: 'error',
  warning: 'warning',
  success: 'success',
  info: 'info',
  accent: 'sparkles',
  default: 'info',
};

/**
 * Inline banner, for an error inside a form rather than instead of a page.
 * Given only a `message` (as a few forms do) it shows that.
 */
export function ErrorAlert({ error, title, message }) {
  const { t } = useTranslation();
  if (!error && !message) return null;

  // Field-level validation errors are shown on the fields themselves, so the
  // banner would just repeat them. If there is a root or unattached message, show it too.
  if (error?.isValidation && Object.keys(error.fieldErrors ?? {}).length) {
    const rootMessage = error.fieldErrors['(root)'] || error.fieldErrors[''];
    return (
      <div className="alert alert-danger" role="alert">
        <span className="alert-icon">
          <Icon name="error" size={16} />
        </span>
        <div>
          <span className="alert-title">{t('errors.checkFields')}</span>
          {rootMessage && <div className="text-sm mt-1">{rootMessage}</div>}
        </div>
      </div>
    );
  }

  const fieldConflicts = error?.fieldConflicts ?? error?.details?.fieldConflicts ?? [];

  return (
    <div className="alert alert-danger" role="alert">
      <span className="alert-icon">
        <Icon name="error" size={16} />
      </span>
      <div>
        {title && <div className="alert-title">{title}</div>}
        <div>{(typeof error === 'string' ? error : error?.message) ?? message ?? t('errors.unexpected')}</div>
        {fieldConflicts.length > 0 && (
          <div className="stack-sm" style={{ marginTop: 8 }}>
            <strong>{t('errors.reviewConflicts')}</strong>
            <ul className="text-sm">
              {fieldConflicts.map((conflict) => (
                <li key={conflict.field}>
                  <strong>{conflict.field}</strong>:{' '}
                  {t('errors.conflict', {
                    submitted: JSON.stringify(conflict.submitted),
                    current: JSON.stringify(conflict.current),
                    bilingual: false,
                  })}
                </li>
              ))}
            </ul>
          </div>
        )}
        {error?.status >= 500 && error?.requestId && (
          <div className="text-xs mono" style={{ marginTop: 4, opacity: 0.8 }}>
            {t('errors.reference', { id: error.requestId, bilingual: false })}
          </div>
        )}
      </div>
    </div>
  );
}

export function Alert({ variant = 'info', title, children, style }) {
  return (
    <div className={`alert alert-${variant}`} role={variant === 'danger' ? 'alert' : 'status'} style={style}>
      <span className="alert-icon">
        <Icon name={ALERT_ICONS[variant] ?? 'info'} size={16} />
      </span>
      <div>
        {title && <div className="alert-title">{title}</div>}
        <div>{children}</div>
      </div>
    </div>
  );
}

/** Shown when the user lacks the permission a screen needs. */
export function NoPermissionState({ what, message }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon="lock"
      title={t('errors.noAccessTitle')}
      message={message ?? t('errors.noAccessMessage', { what: what ?? t('errors.noAccessWhat', { bilingual: false }) })}
    />
  );
}
